import * as angular from 'angular';
import { isValid } from 'date-fns';
import { DataType } from '@mode-vegas/vegas';

import { RangeValueOption, RangeValueOptionRecord, ReportTypes } from '@mode/shared/contract-common';
import { RangeValueFilterControlRecord, ReportFilterControlHelpers } from '@mode/filters/util';
import { formatInUTC, ReportFilterOptionsFactory } from '@mode/shared/util-js';

function makeLabelValueList(obj) {
  const list = [];
  for (const i of Object.getOwnPropertyNames(obj)) {
    list.push({ label: obj[i], value: i });
  }
  return list.sort((a, b) => a.value - b.value);
}
const DatePartLists = {
  [DataType.DATEQTR]: makeLabelValueList(ReportFilterOptionsFactory.QuarterNames),
  [DataType.DATEMONTH]: makeLabelValueList(ReportFilterOptionsFactory.MonthNames),
  [DataType.DATEWKDAY]: makeLabelValueList(ReportFilterOptionsFactory.WeekdayNames),
};

export default class FilterModuleRangeController implements angular.IController {
  static $inject = [];

  filter: RangeValueFilterControlRecord;
  filterOpts: ReportTypes.RangeSelectFilterOptions;
  selected: RangeValueOption;

  // Interaction
  onSelection: ($args: { $filter: RangeValueFilterControlRecord; $value: RangeValueOption }) => void;

  $onInit() {
    this.selected = this.filter.selected;
  }

  get position(): number {
    return this.filter.position;
  }

  get options(): RangeValueOptionRecord {
    return this.filterOpts?.values;
  }

  get isDate(): boolean {
    return ReportFilterControlHelpers.isDateType(this.filter);
  }

  get isDatePart(): boolean {
    return ReportFilterControlHelpers.isDiscreteDateType(this.filter);
  }

  get isNumber(): boolean {
    return ReportFilterControlHelpers.isNumericType(this.filter);
  }

  get dateParts() {
    return DatePartLists[this.filter.formulaType];
  }

  // number range:
  get min(): ReportTypes.ReportRangeFilterValue {
    return this.selected?.min;
  }
  get max(): ReportTypes.ReportRangeFilterValue {
    return this.selected?.max;
  }
  set min(min: ReportTypes.ReportRangeFilterValue) {
    if (min === undefined) {
      min = null;
    }
    if (min !== this.selected?.min) {
      this.selected = this.selected?.with({ min });
      this.onSelection({ $filter: this.filter, $value: this.selected });
    }
  }
  set max(max: ReportTypes.ReportRangeFilterValue) {
    if (max === undefined) {
      max = null;
    }
    if (max !== this.selected?.max) {
      this.selected = this.selected?.with({ max });
      this.onSelection({ $filter: this.filter, $value: this.selected });
    }
  }

  // date range:
  //   Date picker component is
  //   bound with @ so requires strings
  get minDateString(): string {
    const value = new Date(this.min);

    if (this.min && isValid(value)) {
      return formatInUTC(value, 'yyyy-MM-dd');
    } else {
      return null;
    }
  }

  get minDatePlaceholder(): string {
    const min = this.options?.min || this.filter.options.values.min;

    // 0 represents the epoch date (Jan 01 1970)
    if (min || min === 0) {
      const value = new Date(min);
      if (isValid(value)) {
        return formatInUTC(value, 'yyyy-MM-dd');
      } else {
        return 'Invalid minimum date';
      }
    } else {
      return 'Loading';
    }
  }

  get maxDateString(): string {
    const value = new Date(this.max);

    if (this.max && isValid(value)) {
      return formatInUTC(value, 'yyyy-MM-dd');
    } else {
      return null;
    }
  }

  get maxDatePlaceholder(): string {
    const max = this.options?.max || this.filter.options.values.max;

    if (max) {
      const value = new Date(max);

      if (isValid(value)) {
        return formatInUTC(value, 'yyyy-MM-dd');
      } else {
        return 'Invalid maximum date';
      }
    } else {
      return 'Loading';
    }
  }

  setMinDate($value: Date): void {
    const minValue = $value;

    if (typeof minValue === 'undefined') {
      return;
    } else if (minValue === null) {
      this.min = null;
    } else {
      const value = new Date(minValue);

      if (isValid(value)) {
        this.min = value.getTime();
      }
    }
  }

  setMaxDate($value: Date): void {
    const maxValue = $value;

    if (typeof maxValue === 'undefined') {
      return;
    } else if (maxValue === null) {
      this.max = null;
    } else {
      const value = new Date(maxValue);

      if (isValid(value)) {
        this.max = value.getTime();
      }
    }
  }

  // date part range:
  get minDatePart() {
    return String(this.min);
  }
  get maxDatePart() {
    return String(this.max);
  }
  set minDatePart(min: string) {
    this.min = Number(min);
  }
  set maxDatePart(max: string) {
    this.max = Number(max);
  }

  onFocusInput($event): void {
    $event.target.select();
  }
}
